import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const UGCAds = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate("/");
    }, 100);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>UGC Ads | AI Generated UGC Creatives for Meta & TikTok | Fuel72</title>
        <meta
          name="description"
          content="Create high-converting UGC ads with AI. Perfect for Meta Ads, TikTok Ads, and Instagram. No filming required. Professional UGC creatives in 72 hours."
        />
        <meta
          name="keywords"
          content="ugc ads, ugc ad creator, ugc creatives for ads, meta ads ugc, tiktok ugc ads, ugc advertising, ai ugc ads, ugc for facebook ads, instagram ugc ads"
        />
        <link rel="canonical" href="https://www.fuel72.com/ugc-ads" />
        
        {/* Hreflang */}
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/ugc-ads" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/ugc-werbevideo" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/ugc-ads" />
        
        {/* Open Graph */}
        <meta property="og:title" content="UGC Ads | AI Creatives for Meta & TikTok | Fuel72" />
        <meta property="og:description" content="Create UGC ads that convert. AI-powered ad creatives for social media marketing." />
        <meta property="og:url" content="https://www.fuel72.com/ugc-ads" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.fuel72.com/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="UGC Ads | Fuel72" />
        <meta name="twitter:description" content="AI-generated UGC ads for Meta, TikTok, and Instagram campaigns." />

        {/* Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "UGC Ad Creation",
            "name": "AI UGC Ads - High-Converting Ad Creatives",
            "description": "Create professional UGC ads for Meta, TikTok, and Instagram with AI. No filming, no influencers.",
            "provider": { "@type": "Organization", "name": "Fuel72" },
            "areaServed": ["US", "CA", "AU", "GB", "DE", "AT", "CH"]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What are UGC ads?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "UGC ads are advertisements that use user-generated content style to create authentic, relatable marketing. They perform 4x better than traditional ads."
                }
              },
              {
                "@type": "Question",
                "name": "Can AI create UGC ads?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Fuel72 uses AI to create authentic UGC-style ads that look like real creator content. Perfect for Meta, TikTok, and Instagram advertising."
                }
              },
              {
                "@type": "Question",
                "name": "How much do UGC ads cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "With Fuel72, you get 6 UGC ad creatives + posters + captions for $297. Much more affordable than hiring creators."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="min-h-screen gradient-mesh flex items-center justify-center">
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-heading font-bold mb-6">
            UGC Ads That{" "}
            <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
              Convert
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Create high-performing UGC ad creatives for Meta, TikTok, and Instagram. 
            AI-powered, no filming required.
          </p>
          
          <div className="max-w-3xl mx-auto text-left space-y-6">
            <h2 className="text-2xl font-bold">Why UGC Ads Outperform Traditional Ads</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>📈 <strong>4x Higher Engagement</strong> - UGC feels authentic and relatable</li>
              <li>💰 <strong>Lower CPM</strong> - Platforms favor UGC-style content</li>
              <li>🎯 <strong>Better Conversions</strong> - Trust drives purchases</li>
              <li>⚡ <strong>Faster Production</strong> - AI creates in 72 hours</li>
            </ul>
            
            <h2 className="text-2xl font-bold mt-8">Platforms We Create UGC Ads For</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <span className="text-2xl">📘</span>
                <p className="font-medium mt-2">Meta Ads</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <span className="text-2xl">🎵</span>
                <p className="font-medium mt-2">TikTok Ads</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <span className="text-2xl">📸</span>
                <p className="font-medium mt-2">Instagram</p>
              </div>
              <div className="p-4 rounded-lg bg-card/50 text-center">
                <span className="text-2xl">▶️</span>
                <p className="font-medium mt-2">YouTube</p>
              </div>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mt-12">Redirecting to homepage...</p>
        </div>
      </main>
    </>
  );
};

export default UGCAds;
